
import emailjs from '@emailjs/browser';
import { Registrant, EventOption } from '../types';

// EmailJS configuration from environment variables
const EMAILJS_SERVICE_ID = import.meta.env.VITE_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = import.meta.env.VITE_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = import.meta.env.VITE_EMAILJS_PUBLIC_KEY;

// Validate EmailJS configuration
if (!EMAILJS_SERVICE_ID || !EMAILJS_TEMPLATE_ID || !EMAILJS_PUBLIC_KEY) {
  console.error(
    '❌ EmailJS 配置不完整，電子郵件功能將無法使用。\n' +
    '請確認 .env.local 檔案包含所有 EmailJS 相關環境變數。'
  );
}

const formatRegistrantRoles = (registrant: Omit<Registrant, 'id' | 'submissionDate'>) => {
  const roles = registrant.roles && registrant.roles.length > 0 ? registrant.roles : [registrant.role];
  return roles.map(role => {
    if (role === '學生' && registrant.studentSpecialization) {
      return `學生（${registrant.studentSpecialization}` +
        `${registrant.studentSpecialization === '其他（請說明）' && registrant.otherRoleDescription ? `：${registrant.otherRoleDescription}` : ''}）`;
    }
    if (role === '西醫' && registrant.westernMedicineSpecialty) {
      return `西醫（${registrant.westernMedicineSpecialty}）`;
    }
    if (role === '治療師' && registrant.therapistSpecialty) {
      return `治療師（${registrant.therapistSpecialty}）`;
    }
    return role;
  }).join('、');
};

export const sendConfirmationEmail = async (
  registrant: Omit<Registrant, 'id' | 'submissionDate'>,
  allEvents: EventOption[]
) => {
  // Check if EmailJS is properly configured
  if (!EMAILJS_SERVICE_ID || !EMAILJS_TEMPLATE_ID || !EMAILJS_PUBLIC_KEY) {
    console.error('EmailJS configuration is missing, skipping email send');
    return false;
  }

  try {
    const currentYear = new Date().getFullYear();
    const isApproved = registrant.isApproved ?? false;

    // 找出使用者「有勾選」的梯隊完整資料（使用傳入的events）
    const selectedEventsInfo = allEvents.filter(evt =>
      registrant.selectedEvents.includes(evt.id)
    );

    // --- 建構優化版的視覺化內容 ---
    let fullContent = "";

    // 1. 溫馨的開頭（根據審核狀態調整）
    fullContent += `親愛的 ${registrant.name} 平安：\n\n`;
    fullContent += `感謝您報名「${currentYear}年 醫宣義診梯隊」。\n`;

    if (isApproved) {
      // 已核准：報名成功
      fullContent += `您的報名已完成登記！這是一份您的報名確認憑證，請核對下方資訊。\n\n`;
    } else {
      // 待審核：已收到報名
      fullContent += `我們已收到您的報名申請！這是一份您的報名確認憑證，請核對下方資訊。\n\n`;
    }
    
    // 2. 視覺分隔線與個人資料區 (精簡版)
    fullContent += `━━━━━━━━━━━━━━━━━━━━━━\n`;
    fullContent += `    👤  個 人 資 料 確 認\n`;
    fullContent += `━━━━━━━━━━━━━━━━━━━━━━\n`;
    fullContent += `● 姓名：${registrant.name} (${registrant.gender})\n`;
    fullContent += `● 教會：${registrant.church} (${registrant.district})\n`;
    fullContent += `● 身分：${formatRegistrantRoles(registrant)}`;
    if (registrant.otherRoleDescription) fullContent += ` - ${registrant.otherRoleDescription}`;
    fullContent += `\n`;
    fullContent += `● 電話：${registrant.phone}\n`;
    fullContent += `● 信箱：${registrant.email}\n\n`;

    // 3. 梯隊列表 (重點：只列出參加的，並且排版整齊)
    fullContent += `━━━━━━━━━━━━━━━━━━━━━━\n`;
    fullContent += `    📅  已 報 名 梯 隊  (共${selectedEventsInfo.length}場)\n`;
    fullContent += `━━━━━━━━━━━━━━━━━━━━━━\n\n`;

    if (selectedEventsInfo.length > 0) {
      selectedEventsInfo.forEach((evt, index) => {
        // 使用編號與 Emoji 增加易讀性
        fullContent += `${index + 1}. 【${evt.dateRange}】\n`;
        fullContent += `    📍 地點：${evt.location}\n`;
        fullContent += `    ⚠️ 截止：${evt.deadline}\n\n`;
      });
    } else {
      fullContent += `(目前尚未勾選任何梯隊)\n\n`;
    }

    // 4. 備註事項 (如果有才顯示)
    if (registrant.message) {
      fullContent += `━━━━━━━━━━━━━━━━━━━━━━\n`;
      fullContent += `    💬  您 的 備 註\n`;
      fullContent += `━━━━━━━━━━━━━━━━━━━━━━\n`;
      fullContent += `${registrant.message}\n\n`;
    }

    // 5. 結尾提醒（根據審核狀態調整）
    fullContent += `================================\n`;

    if (isApproved) {
      // 已核准：可以在分工名單中查看
      fullContent += `✅ 您的報名已完成，現在可以在「分工名單」中查看您的報名資訊。\n\n`;
    } else {
      // 待審核：說明後續流程
      fullContent += `📋 您的報名申請已送出，目前正在審核中。\n`;
      fullContent += `📧 審核通過後，我們將會另外寄送「正式的報名成功確認信」，屆時您才能正式參與梯隊活動。\n`;
      fullContent += `📮 請務必留意您的信箱，等候後續通知。感謝您的耐心等候！\n\n`;
    }

    fullContent += `※ 這是系統自動發送的信件，請勿直接回覆。\n`;
    fullContent += `※ 若需修改報名資料，請聯繫醫宣行政團隊。\n\n`;
    fullContent += `願主紀念您的付出！\n`;
    fullContent += `TJC 醫宣行政團隊 敬上`;

    // 設定要傳給 EmailJS 的變數
    // 注意: 這裡的 key (例如 email, subject) 必須對應 EmailJS Template 中的 {{variable}}
    const emailSubject = isApproved
      ? `【報名成功】${currentYear}年醫宣義診 - ${registrant.name} 報名確認憑證`
      : `【已受理報名】${currentYear}年醫宣義診 - ${registrant.name} 報名確認憑證`;

    const templateParams = {
      to_name: registrant.name,
      email: registrant.email, // 修正：對應模板中的 {{email}}
      subject: emailSubject,
      message_body: fullContent,
    };

    const response = await emailjs.send(
      EMAILJS_SERVICE_ID as string,
      EMAILJS_TEMPLATE_ID as string,
      templateParams,
      EMAILJS_PUBLIC_KEY as string
    );

    console.log('Email sent successfully!', response.status, response.text);
    return true;
  } catch (error) {
    console.error('Failed to send email:', error);
    return false;
  }
};
